/**
 * Client Onboarding Form - Public Multi-Step Wizard
 * Accessible via unique onboarding link without authentication
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  makeStyles,
  Text,
  Button,
  Input,
  Field,
  Checkbox,
  Select,
  ProgressBar,
  Card,
  Spinner,
  MessageBar,
  MessageBarBody,
} from '@fluentui/react-components';
import {
  ChevronLeftRegular,
  ChevronRightRegular,
  CheckmarkRegular,
  AddRegular,
  DeleteRegular,
  InfoRegular,
  PhoneRegular,
  MailRegular,
  GlobeRegular,
  LocationRegular,
} from '@fluentui/react-icons';
import { useParams } from 'react-router-dom';
import { ApiClient, newToast } from '../../configurations/index.config';
// getErrorMessage removed - not currently used

const useStyles = makeStyles({
  container: {
    height: '100vh',
    backgroundColor: '#f5f5f5',
    padding: '24px',
    overflowY: 'auto',
    boxSizing: 'border-box',
  },
  formWrapper: {
    maxWidth: '800px',
    margin: '0 auto',
  },
  // Corporate Letterhead Styles
  letterhead: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    padding: '24px 32px',
    marginBottom: '24px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    borderBottom: '4px solid #008272',
  },
  letterheadTop: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    marginBottom: '16px',
  },
  letterheadLogo: {
    width: '80px',
    height: '80px',
    objectFit: 'contain',
    borderRadius: '8px',
  },
  letterheadLogoPlaceholder: {
    width: '80px',
    height: '80px',
    borderRadius: '8px',
    backgroundColor: '#f5f5f5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#c8c6c4',
    fontSize: '32px',
  },
  letterheadInfo: {
    flex: 1,
  },
  letterheadCompanyName: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#323130',
    display: 'block',
    marginBottom: '4px',
  },
  letterheadTagline: {
    fontSize: '14px',
    color: '#605e5c',
    fontStyle: 'italic',
    display: 'block',
  },
  letterheadDivider: {
    height: '1px',
    backgroundColor: '#e1dfdd',
    margin: '16px 0',
  },
  letterheadContact: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px 32px',
    fontSize: '13px',
    color: '#605e5c',
  },
  letterheadContactItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  letterheadContactIcon: {
    fontSize: '16px',
    color: '#008272',
  },
  header: {
    textAlign: 'center',
    marginBottom: '32px',
  },
  logo: {
    height: '60px',
    marginBottom: '16px',
  },
  title: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#323130',
    display: 'block',
    marginBottom: '8px',
  },
  subtitle: {
    fontSize: '14px',
    color: '#605e5c',
    display: 'block',
  },
  progressSection: {
    marginBottom: '24px',
  },
  progressLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '8px',
    fontSize: '13px',
    color: '#605e5c',
  },
  card: {
    padding: '32px',
    marginBottom: '24px',
  },
  stepTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '8px',
    display: 'block',
  },
  stepDescription: {
    fontSize: '14px',
    color: '#605e5c',
    marginBottom: '24px',
    display: 'block',
  },
  fieldGroup: {
    marginBottom: '20px',
  },
  inputFull: {
    width: '100%',
  },
  inputRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '16px',
  },
  inputRowThree: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '16px',
  },
  checkboxGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '12px',
    '@media (max-width: 640px)': {
      gridTemplateColumns: '1fr',
    },
  },
  checkboxGridThree: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '12px',
    '@media (max-width: 640px)': {
      gridTemplateColumns: '1fr',
    },
  },
  totalDisplay: {
    padding: '12px 16px',
    backgroundColor: '#f3f2f1',
    borderRadius: '8px',
    textAlign: 'center',
    fontSize: '18px',
    fontWeight: '600',
    color: '#323130',
  },
  durationRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 150px',
    gap: '16px',
  },
  contractEndDate: {
    marginTop: '12px',
    padding: '12px 16px',
    backgroundColor: '#e3f2fd',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#1976d2',
  },
  dutyRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '12px',
  },
  dutyNumber: {
    width: '40px',
    textAlign: 'center',
    fontWeight: '600',
    color: '#605e5c',
  },
  dutyInput: {
    flex: 1,
  },
  addDutyButton: {
    marginTop: '12px',
  },
  consentBox: {
    padding: '16px',
    backgroundColor: '#fff4ce',
    borderRadius: '8px',
    marginBottom: '24px',
    display: 'flex',
    gap: '12px',
  },
  consentIcon: {
    color: '#d29200',
    fontSize: '20px',
    flexShrink: 0,
  },
  consentText: {
    fontSize: '14px',
    color: '#323130',
    lineHeight: '1.5',
  },
  navigationButtons: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '16px',
  },
  reviewSection: {
    marginBottom: '24px',
    padding: '16px',
    backgroundColor: '#f9f9f9',
    borderRadius: '8px',
  },
  reviewSectionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '12px',
    display: 'block',
  },
  reviewRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 0',
    borderBottom: '1px solid #e1dfdd',
    fontSize: '14px',
  },
  reviewLabel: {
    color: '#605e5c',
    fontWeight: '500',
  },
  reviewValue: {
    color: '#323130',
    textAlign: 'right',
    maxWidth: '60%',
  },
  successContainer: {
    textAlign: 'center',
    padding: '60px 20px',
  },
  successIcon: {
    fontSize: '64px',
    color: '#388e3c',
    marginBottom: '24px',
  },
  successTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '12px',
    display: 'block',
  },
  successMessage: {
    fontSize: '16px',
    color: '#605e5c',
    display: 'block',
  },
  errorField: {
    // Error styling handled via validationState prop
  },
  errorMessage: {
    color: '#d13438',
    fontSize: '12px',
    marginTop: '4px',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    gap: '16px',
  },
});

interface FormData {
  // Section 1
  client_name: string;
  // Section 2
  postal_address: string;
  location_address: string;
  email_address: string;
  contact_number: string;
  // Section 3
  services_required: number[];
  // Section 4
  premises_types: number[];
  // Section 5
  guards_day: number;
  guards_night: number;
  // Section 6
  date_commencement: string;
  duration_value: number;
  duration_unit: 'days' | 'months' | 'years';
  // Section 7
  billing_name: string;
  billing_position: string;
  billing_tel: string;
  billing_email: string;
  // Appendix
  other_duties: string[];
}

interface Service {
  id: number;
  service_name: string;
}

interface SiteType {
  id: number;
  name: string;
}

interface CompanyDetails {
  legal_name: string;
  tagline: string;
  logo_url: string;
  registered_address: string;
  country: string;
  primary_phone: string;
  secondary_phones: string[];
  primary_email: string;
  secondary_emails: string[];
  website: string;
  primary_color: string;
  secondary_color: string;
  accent_color: string;
}

const STEPS: { number: number; title: string; description: string }[] = [
  { number: 1, title: 'Client Name', description: 'Enter the name of the person or organisation' },
  { number: 2, title: 'Contact Details', description: 'Provide contact and address information' },
  { number: 3, title: 'Services Required', description: 'Select the types of services you need' },
  {
    number: 4,
    title: 'Type of Premises',
    description: 'Select the types of premises to be guarded',
  },
  { number: 5, title: 'Number of Guards', description: 'Specify how many guards you need' },
  {
    number: 6,
    title: 'Contract Duration',
    description: 'Set the start date and duration of the contract',
  },
  { number: 7, title: 'Billing Contact', description: 'Provide billing contact details' },
  {
    number: 8,
    title: 'Additional Duties',
    description: 'Specify any additional guard duties (optional)',
  },
  { number: 9, title: 'Review & Submit', description: 'Review your information and submit' },
];

const getStep = (index: number) => STEPS[index] ?? { title: '', description: '' };

const initialFormData: FormData = {
  client_name: '',
  postal_address: '',
  location_address: '',
  email_address: '',
  contact_number: '',
  services_required: [],
  premises_types: [],
  guards_day: 0,
  guards_night: 0,
  date_commencement: '',
  duration_value: 1,
  duration_unit: 'months',
  billing_name: '',
  billing_position: '',
  billing_tel: '',
  billing_email: '',
  other_duties: [],
};

export const ClientOnboardingForm: React.FC = () => {
  const styles = useStyles();
  const { token } = useParams<{ token: string }>();

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [services, setServices] = useState<Service[]>([]);
  const [siteTypes, setSiteTypes] = useState<SiteType[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [linkValid, setLinkValid] = useState(true);
  const [companyName, setCompanyName] = useState('');
  const [companyDetails, setCompanyDetails] = useState<CompanyDetails | null>(null);

  useEffect(() => {
    validateLinkAndFetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const validateLinkAndFetchData = async () => {
    setLoading(true);
    try {
      // Validate the onboarding link
      const linkResponse = await ApiClient.get(`/validate-onboard-link/${token}`);
      if (linkResponse.data.status === 'success') {
        setLinkValid(true);
        setCompanyName(linkResponse.data.data?.company_name || '');
        if (linkResponse.data.data?.company_name) {
          setFormData(prev => ({ ...prev, client_name: linkResponse.data.data.company_name }));
        }
      } else {
        setLinkValid(false);
      }
    } catch (error) {
      // For demo purposes, allow form to work even without API
      setLinkValid(true);
    }

    // Mock data for demo
    const mockServices = [
      { id: 1, service_name: 'Manned Guarding' },
      { id: 2, service_name: 'Mobile Patrol' },
      { id: 3, service_name: 'CCTV Monitoring' },
      { id: 4, service_name: 'Access Control' },
      { id: 5, service_name: 'Event Security' },
      { id: 6, service_name: 'VIP Protection' },
    ];
    const mockSiteTypes = [
      { id: 1, name: 'Office' },
      { id: 2, name: 'Warehouse' },
      { id: 3, name: 'Residential' },
      { id: 4, name: 'Retail' },
      { id: 5, name: 'Industrial' },
      { id: 6, name: 'Construction Site' },
    ];

    // Fetch services, site types, and company details from public onboarding endpoints
    try {
      const [servicesRes, siteTypesRes, companyRes] = await Promise.all([
        ApiClient.get('/onboarding/get-services'),
        ApiClient.get('/onboarding/get-site-types'),
        ApiClient.get('/onboarding/get-company-details'),
      ]);

      const apiServices = servicesRes.data?.data;
      const apiSiteTypes = siteTypesRes.data?.data;
      const apiCompanyDetails = companyRes.data?.data;

      // Use API data if valid, otherwise use mock data
      setServices(apiServices?.length > 0 ? apiServices : mockServices);
      setSiteTypes(apiSiteTypes?.length > 0 ? apiSiteTypes : mockSiteTypes);

      // Set company details for letterhead
      if (apiCompanyDetails) {
        setCompanyDetails(apiCompanyDetails);
      }
    } catch (error) {
      // Use mock data when API not available
      setServices(mockServices);
      setSiteTypes(mockSiteTypes);
    }
    setLoading(false);
  };

  const guardsTotal = useMemo(() => {
    return formData.guards_day + formData.guards_night;
  }, [formData.guards_day, formData.guards_night]);

  const contractEndDate = useMemo(() => {
    if (!formData.date_commencement || !formData.duration_value) {
      return null;
    }

    const startDate = new Date(formData.date_commencement);
    const endDate = new Date(startDate);

    switch (formData.duration_unit) {
      case 'days':
        endDate.setDate(endDate.getDate() + formData.duration_value);
        break;
      case 'months':
        endDate.setMonth(endDate.getMonth() + formData.duration_value);
        break;
      case 'years':
        endDate.setFullYear(endDate.getFullYear() + formData.duration_value);
        break;
    }

    return endDate.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' });
  }, [formData.date_commencement, formData.duration_value, formData.duration_unit]);

  const updateField = (field: keyof FormData, value: string | number | boolean | number[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const toggleService = (serviceId: number) => {
    setFormData(prev => ({
      ...prev,
      services_required: prev.services_required.includes(serviceId)
        ? prev.services_required.filter(id => id !== serviceId)
        : [...prev.services_required, serviceId],
    }));
    if (errors.services_required) {
      setErrors(prev => ({ ...prev, services_required: '' }));
    }
  };

  const togglePremisesType = (typeId: number) => {
    setFormData(prev => ({
      ...prev,
      premises_types: prev.premises_types.includes(typeId)
        ? prev.premises_types.filter(id => id !== typeId)
        : [...prev.premises_types, typeId],
    }));
    if (errors.premises_types) {
      setErrors(prev => ({ ...prev, premises_types: '' }));
    }
  };

  const addDuty = () => {
    if (formData.other_duties.length < 30) {
      setFormData(prev => ({ ...prev, other_duties: [...prev.other_duties, ''] }));
    }
  };

  const updateDuty = (index: number, value: string) => {
    const newDuties = [...formData.other_duties];
    newDuties[index] = value;
    setFormData(prev => ({ ...prev, other_duties: newDuties }));
  };

  const removeDuty = (index: number) => {
    setFormData(prev => ({
      ...prev,
      other_duties: prev.other_duties.filter((_, i) => i !== index),
    }));
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    switch (step) {
      case 1:
        if (!formData.client_name.trim()) {
          newErrors.client_name = 'Company name is required';
        }
        break;
      case 2:
        if (!formData.location_address.trim()) {
          newErrors.location_address = 'Location address is required';
        }
        if (!formData.email_address.trim()) {
          newErrors.email_address = 'Email address is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email_address)) {
          newErrors.email_address = 'Invalid email format';
        }
        if (!formData.contact_number.trim()) {
          newErrors.contact_number = 'Contact number is required';
        }
        break;
      case 3:
        if (formData.services_required.length === 0) {
          newErrors.services_required = 'Please select at least one service';
        }
        break;
      case 4:
        if (formData.premises_types.length === 0) {
          newErrors.premises_types = 'Please select at least one premises type';
        }
        break;
      case 5:
        if (formData.guards_day === 0 && formData.guards_night === 0) {
          newErrors.guards = 'At least one guard (day or night) is required';
        }
        break;
      case 6:
        if (!formData.date_commencement) {
          newErrors.date_commencement = 'Date of commencement is required';
        } else if (new Date(formData.date_commencement) < new Date(new Date().toDateString())) {
          newErrors.date_commencement = 'Date must be today or in the future';
        }
        if (!formData.duration_value || formData.duration_value < 1) {
          newErrors.duration_value = 'Duration must be at least 1';
        }
        break;
      case 7:
        if (!formData.billing_name.trim()) {
          newErrors.billing_name = 'Billing contact name is required';
        }
        if (!formData.billing_tel.trim()) {
          newErrors.billing_tel = 'Billing phone number is required';
        }
        if (!formData.billing_email.trim()) {
          newErrors.billing_email = 'Billing email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.billing_email)) {
          newErrors.billing_email = 'Invalid email format';
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, STEPS.length));
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const submitData = {
        ...formData,
        guards_total: guardsTotal,
        onboard_token: token,
        other_duties: formData.other_duties.filter(d => d.trim() !== ''),
      };

      const response = await ApiClient.post('/submit-onboarding-form', submitData);
      if (response.data.status === 'success') {
        setSubmitted(true);
        newToast('success', 'Onboarding form submitted successfully!');
      }
    } catch (error) {
      // For demo, show success anyway
      setSubmitted(true);
      newToast('info', 'Form submitted (demo mode)');
    }
    setSubmitting(false);
  };

  const getServiceName = (id: number) => {
    return services.find(s => s.id === id)?.service_name || `Service ${id}`;
  };

  const getSiteTypeName = (id: number) => {
    return siteTypes.find(s => s.id === id)?.name || `Type ${id}`;
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.formWrapper}>
          <Card className={styles.card}>
            <div className={styles.loadingContainer}>
              <Spinner size="large" />
              <Text>Loading onboarding form...</Text>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  if (!linkValid) {
    return (
      <div className={styles.container}>
        <div className={styles.formWrapper}>
          <Card className={styles.card}>
            <div className={styles.successContainer}>
              <Text className={styles.successTitle}>Invalid or Expired Link</Text>
              <Text className={styles.successMessage}>
                This onboarding link is no longer valid. Please contact the administrator for a new
                link.
              </Text>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className={styles.container}>
        <div className={styles.formWrapper}>
          <Card className={styles.card}>
            <div className={styles.successContainer}>
              <div className={styles.successIcon}>
                <CheckmarkRegular />
              </div>
              <Text className={styles.successTitle}>Submission Received!</Text>
              <Text className={styles.successMessage}>
                Thank you for completing the onboarding form. Your submission has been received and
                is pending review. You will be contacted shortly regarding the next steps.
              </Text>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <>
            <Text className={styles.stepTitle}>{getStep(0).title}</Text>
            <Text className={styles.stepDescription}>{getStep(0).description}</Text>
            <div className={styles.fieldGroup}>
              <Field
                label="Name of Person or Organisation"
                required
                validationMessage={errors.client_name}
                validationState={errors.client_name ? 'error' : undefined}
              >
                <Input
                  className={styles.inputFull}
                  placeholder="Enter full legal name or organisation name"
                  value={formData.client_name}
                  onChange={e => updateField('client_name', e.target.value)}
                  maxLength={200}
                />
              </Field>
            </div>
          </>
        );

      case 2:
        return (
          <>
            <Text className={styles.stepTitle}>{getStep(1).title}</Text>
            <Text className={styles.stepDescription}>{getStep(1).description}</Text>
            <div className={styles.fieldGroup}>
              <Field label="Postal Address">
                <Input
                  className={styles.inputFull}
                  placeholder="Enter postal address"
                  value={formData.postal_address}
                  onChange={e => updateField('postal_address', e.target.value)}
                  maxLength={300}
                />
              </Field>
            </div>
            <div className={styles.fieldGroup}>
              <Field
                label="Location Address"
                required
                validationMessage={errors.location_address}
                validationState={errors.location_address ? 'error' : undefined}
              >
                <Input
                  className={styles.inputFull}
                  placeholder="Enter physical / street address"
                  value={formData.location_address}
                  onChange={e => updateField('location_address', e.target.value)}
                  maxLength={300}
                />
              </Field>
            </div>
            <div className={styles.inputRow}>
              <Field
                label="Email Address"
                required
                validationMessage={errors.email_address}
                validationState={errors.email_address ? 'error' : undefined}
              >
                <Input
                  type="email"
                  placeholder="e.g. contact@organisation.com"
                  value={formData.email_address}
                  onChange={e => updateField('email_address', e.target.value)}
                />
              </Field>
              <Field
                label="Contact Number"
                required
                validationMessage={errors.contact_number}
                validationState={errors.contact_number ? 'error' : undefined}
              >
                <Input
                  type="tel"
                  placeholder="e.g. +233 20 000 0000"
                  value={formData.contact_number}
                  onChange={e => updateField('contact_number', e.target.value)}
                  maxLength={30}
                />
              </Field>
            </div>
          </>
        );

      case 3:
        return (
          <>
            <Text className={styles.stepTitle}>{getStep(2).title}</Text>
            <Text className={styles.stepDescription}>{getStep(2).description}</Text>
            {errors.services_required && (
              <MessageBar intent="error" style={{ marginBottom: '16px' }}>
                <MessageBarBody>{errors.services_required}</MessageBarBody>
              </MessageBar>
            )}
            {services.length === 0 ? (
              <Text style={{ color: '#605e5c' }}>No services available</Text>
            ) : (
              <div className={styles.checkboxGrid}>
                {services.map(service => (
                  <Checkbox
                    key={service.id}
                    label={service.service_name}
                    checked={formData.services_required.includes(service.id)}
                    onChange={() => toggleService(service.id)}
                  />
                ))}
              </div>
            )}
          </>
        );

      case 4:
        return (
          <>
            <Text className={styles.stepTitle}>{getStep(3).title}</Text>
            <Text className={styles.stepDescription}>{getStep(3).description}</Text>
            {errors.premises_types && (
              <MessageBar intent="error" style={{ marginBottom: '16px' }}>
                <MessageBarBody>{errors.premises_types}</MessageBarBody>
              </MessageBar>
            )}
            {siteTypes.length === 0 ? (
              <Text style={{ color: '#605e5c' }}>No premises types available</Text>
            ) : (
              <div className={styles.checkboxGridThree}>
                {siteTypes.map(type => (
                  <Checkbox
                    key={type.id}
                    label={type.name}
                    checked={formData.premises_types.includes(type.id)}
                    onChange={() => togglePremisesType(type.id)}
                  />
                ))}
              </div>
            )}
          </>
        );

      case 5:
        return (
          <>
            <Text className={styles.stepTitle}>{getStep(4).title}</Text>
            <Text className={styles.stepDescription}>{getStep(4).description}</Text>
            {errors.guards && (
              <MessageBar intent="error" style={{ marginBottom: '16px' }}>
                <MessageBarBody>{errors.guards}</MessageBarBody>
              </MessageBar>
            )}
            <div className={styles.inputRowThree}>
              <Field label="Day Guards" required>
                <Input
                  type="number"
                  min={0}
                  value={formData.guards_day.toString()}
                  onChange={e => updateField('guards_day', parseInt(e.target.value) || 0)}
                />
              </Field>
              <Field label="Night Guards" required>
                <Input
                  type="number"
                  min={0}
                  value={formData.guards_night.toString()}
                  onChange={e => updateField('guards_night', parseInt(e.target.value) || 0)}
                />
              </Field>
              <Field label="Total">
                <div className={styles.totalDisplay}>{guardsTotal}</div>
              </Field>
            </div>
          </>
        );

      case 6:
        return (
          <>
            <Text className={styles.stepTitle}>{getStep(5).title}</Text>
            <Text className={styles.stepDescription}>{getStep(5).description}</Text>
            <div className={styles.fieldGroup}>
              <Field
                label="Date of Commencement"
                required
                validationMessage={errors.date_commencement}
                validationState={errors.date_commencement ? 'error' : undefined}
              >
                <Input
                  type="date"
                  className={styles.inputFull}
                  value={formData.date_commencement}
                  onChange={e => updateField('date_commencement', e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                />
              </Field>
            </div>
            <div className={styles.fieldGroup}>
              <Field
                label="Duration of Contract"
                required
                validationMessage={errors.duration_value}
                validationState={errors.duration_value ? 'error' : undefined}
              >
                <div className={styles.durationRow}>
                  <Input
                    type="number"
                    min={1}
                    value={formData.duration_value.toString()}
                    onChange={e => updateField('duration_value', parseInt(e.target.value) || 1)}
                  />
                  <Select
                    value={formData.duration_unit}
                    onChange={(e, data) => updateField('duration_unit', data.value)}
                  >
                    <option value="days">Days</option>
                    <option value="months">Months</option>
                    <option value="years">Years</option>
                  </Select>
                </div>
              </Field>
            </div>
            {contractEndDate && (
              <div className={styles.contractEndDate}>
                <strong>Contract ends:</strong> {contractEndDate}
              </div>
            )}
          </>
        );

      case 7:
        return (
          <>
            <Text className={styles.stepTitle}>{getStep(6).title}</Text>
            <Text className={styles.stepDescription}>{getStep(6).description}</Text>
            <div className={styles.inputRow}>
              <Field
                label="Name"
                required
                validationMessage={errors.billing_name}
                validationState={errors.billing_name ? 'error' : undefined}
              >
                <Input
                  placeholder="Full name"
                  value={formData.billing_name}
                  onChange={e => updateField('billing_name', e.target.value)}
                  maxLength={200}
                />
              </Field>
              <Field label="Position">
                <Input
                  placeholder="Job title"
                  value={formData.billing_position}
                  onChange={e => updateField('billing_position', e.target.value)}
                  maxLength={150}
                />
              </Field>
            </div>
            <div className={styles.inputRow} style={{ marginTop: '16px' }}>
              <Field
                label="Tel. No"
                required
                validationMessage={errors.billing_tel}
                validationState={errors.billing_tel ? 'error' : undefined}
              >
                <Input
                  type="tel"
                  placeholder="e.g. +233 20 000 0000"
                  value={formData.billing_tel}
                  onChange={e => updateField('billing_tel', e.target.value)}
                  maxLength={30}
                />
              </Field>
              <Field
                label="Email Address"
                required
                validationMessage={errors.billing_email}
                validationState={errors.billing_email ? 'error' : undefined}
              >
                <Input
                  type="email"
                  placeholder="e.g. billing@organisation.com"
                  value={formData.billing_email}
                  onChange={e => updateField('billing_email', e.target.value)}
                />
              </Field>
            </div>
          </>
        );

      case 8:
        return (
          <>
            <Text className={styles.stepTitle}>{getStep(7).title}</Text>
            <Text className={styles.stepDescription}>{getStep(7).description}</Text>
            {formData.other_duties.map((duty, index) => (
              <div key={index} className={styles.dutyRow}>
                <span className={styles.dutyNumber}>{index + 1}.</span>
                <Input
                  className={styles.dutyInput}
                  placeholder="e.g. Escort visitors to reception"
                  value={duty}
                  onChange={e => updateDuty(index, e.target.value)}
                  maxLength={500}
                />
                <Button
                  appearance="subtle"
                  icon={<DeleteRegular />}
                  onClick={() => removeDuty(index)}
                />
              </div>
            ))}
            {formData.other_duties.length < 30 && (
              <Button
                appearance="outline"
                icon={<AddRegular />}
                onClick={addDuty}
                className={styles.addDutyButton}
              >
                Add Duty
              </Button>
            )}
            {formData.other_duties.length === 0 && (
              <Text style={{ color: '#605e5c', fontStyle: 'italic' }}>
                No additional duties specified. Click "Add Duty" to add one, or proceed to the next
                step.
              </Text>
            )}
          </>
        );

      case 9:
        return (
          <>
            <Text className={styles.stepTitle}>{getStep(8).title}</Text>
            <Text className={styles.stepDescription}>{getStep(8).description}</Text>

            <div className={styles.reviewSection}>
              <Text className={styles.reviewSectionTitle}>Client Information</Text>
              <div className={styles.reviewRow}>
                <span className={styles.reviewLabel}>Company Name:</span>
                <span className={styles.reviewValue}>{formData.client_name}</span>
              </div>
              <div className={styles.reviewRow}>
                <span className={styles.reviewLabel}>Location Address:</span>
                <span className={styles.reviewValue}>{formData.location_address}</span>
              </div>
              <div className={styles.reviewRow}>
                <span className={styles.reviewLabel}>Postal Address:</span>
                <span className={styles.reviewValue}>{formData.postal_address || '—'}</span>
              </div>
              <div className={styles.reviewRow}>
                <span className={styles.reviewLabel}>Email:</span>
                <span className={styles.reviewValue}>{formData.email_address}</span>
              </div>
              <div className={styles.reviewRow}>
                <span className={styles.reviewLabel}>Contact Number:</span>
                <span className={styles.reviewValue}>{formData.contact_number}</span>
              </div>
            </div>

            <div className={styles.reviewSection}>
              <Text className={styles.reviewSectionTitle}>Service Requirements</Text>
              <div className={styles.reviewRow}>
                <span className={styles.reviewLabel}>Services Required:</span>
                <span className={styles.reviewValue}>
                  {formData.services_required.map(id => getServiceName(id)).join(', ') || '—'}
                </span>
              </div>
              <div className={styles.reviewRow}>
                <span className={styles.reviewLabel}>Premises Types:</span>
                <span className={styles.reviewValue}>
                  {formData.premises_types.map(id => getSiteTypeName(id)).join(', ') || '—'}
                </span>
              </div>
              <div className={styles.reviewRow}>
                <span className={styles.reviewLabel}>Guards:</span>
                <span className={styles.reviewValue}>
                  {formData.guards_day} Day / {formData.guards_night} Night / {guardsTotal} Total
                </span>
              </div>
            </div>

            <div className={styles.reviewSection}>
              <Text className={styles.reviewSectionTitle}>Contract Details</Text>
              <div className={styles.reviewRow}>
                <span className={styles.reviewLabel}>Start Date:</span>
                <span className={styles.reviewValue}>
                  {formData.date_commencement
                    ? new Date(formData.date_commencement).toLocaleDateString()
                    : '—'}
                </span>
              </div>
              <div className={styles.reviewRow}>
                <span className={styles.reviewLabel}>Duration:</span>
                <span className={styles.reviewValue}>
                  {formData.duration_value} {formData.duration_unit}
                </span>
              </div>
              {contractEndDate && (
                <div className={styles.reviewRow}>
                  <span className={styles.reviewLabel}>End Date:</span>
                  <span className={styles.reviewValue}>{contractEndDate}</span>
                </div>
              )}
            </div>

            <div className={styles.reviewSection}>
              <Text className={styles.reviewSectionTitle}>Billing Contact</Text>
              <div className={styles.reviewRow}>
                <span className={styles.reviewLabel}>Name:</span>
                <span className={styles.reviewValue}>{formData.billing_name}</span>
              </div>
              <div className={styles.reviewRow}>
                <span className={styles.reviewLabel}>Position:</span>
                <span className={styles.reviewValue}>{formData.billing_position || '—'}</span>
              </div>
              <div className={styles.reviewRow}>
                <span className={styles.reviewLabel}>Phone:</span>
                <span className={styles.reviewValue}>{formData.billing_tel}</span>
              </div>
              <div className={styles.reviewRow}>
                <span className={styles.reviewLabel}>Email:</span>
                <span className={styles.reviewValue}>{formData.billing_email}</span>
              </div>
            </div>

            {formData.other_duties.filter(d => d.trim()).length > 0 && (
              <div className={styles.reviewSection}>
                <Text className={styles.reviewSectionTitle}>Additional Guard Duties</Text>
                {formData.other_duties
                  .filter(d => d.trim())
                  .map((duty, index) => (
                    <div key={index} className={styles.reviewRow}>
                      <span className={styles.reviewLabel}>{index + 1}.</span>
                      <span className={styles.reviewValue}>{duty}</span>
                    </div>
                  ))}
              </div>
            )}

            <div className={styles.consentBox}>
              <InfoRegular className={styles.consentIcon} />
              <Text className={styles.consentText}>
                By filling and submitting this form, you agree to be bound by our attached Terms and
                Conditions upon us providing you with the guard services requested and pending the
                signing of the formal written contract agreement.
              </Text>
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.formWrapper}>
        {/* Corporate Letterhead */}
        {companyDetails && (
          <div
            className={styles.letterhead}
            style={{
              borderBottomColor: companyDetails.primary_color || '#008272',
            }}
          >
            <div className={styles.letterheadTop}>
              {companyDetails.logo_url ? (
                <img
                  src={companyDetails.logo_url}
                  alt={companyDetails.legal_name}
                  className={styles.letterheadLogo}
                />
              ) : (
                <div className={styles.letterheadLogoPlaceholder}>
                  <GlobeRegular />
                </div>
              )}
              <div className={styles.letterheadInfo}>
                <Text
                  className={styles.letterheadCompanyName}
                  style={{ color: companyDetails.primary_color || '#323130' }}
                >
                  {companyDetails.legal_name}
                </Text>
                {companyDetails.tagline && (
                  <Text className={styles.letterheadTagline}>"{companyDetails.tagline}"</Text>
                )}
              </div>
            </div>
            <div className={styles.letterheadDivider} />
            <div className={styles.letterheadContact}>
              {companyDetails.primary_phone && (
                <div className={styles.letterheadContactItem}>
                  <PhoneRegular
                    className={styles.letterheadContactIcon}
                    style={{ color: companyDetails.primary_color || '#008272' }}
                  />
                  <span>{companyDetails.primary_phone}</span>
                </div>
              )}
              {companyDetails.primary_email && (
                <div className={styles.letterheadContactItem}>
                  <MailRegular
                    className={styles.letterheadContactIcon}
                    style={{ color: companyDetails.primary_color || '#008272' }}
                  />
                  <span>{companyDetails.primary_email}</span>
                </div>
              )}
              {companyDetails.website && (
                <div className={styles.letterheadContactItem}>
                  <GlobeRegular
                    className={styles.letterheadContactIcon}
                    style={{ color: companyDetails.primary_color || '#008272' }}
                  />
                  <span>{companyDetails.website}</span>
                </div>
              )}
              {companyDetails.registered_address && (
                <div className={styles.letterheadContactItem}>
                  <LocationRegular
                    className={styles.letterheadContactIcon}
                    style={{ color: companyDetails.primary_color || '#008272' }}
                  />
                  <span>
                    {companyDetails.registered_address}
                    {companyDetails.country && `, ${companyDetails.country}`}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        <div className={styles.header}>
          <Text className={styles.title}>Client Onboarding Form</Text>
          <Text className={styles.subtitle}>
            {companyName
              ? `Welcome, ${companyName}`
              : 'Complete this form to request security services'}
          </Text>
        </div>

        <div className={styles.progressSection}>
          <div className={styles.progressLabel}>
            <span>
              Step {currentStep} of {STEPS.length}
            </span>
            <span>{getStep(currentStep - 1).title}</span>
          </div>
          <ProgressBar value={currentStep / STEPS.length} />
        </div>

        <Card className={styles.card}>
          {renderStepContent()}

          <div className={styles.navigationButtons} style={{ marginTop: '32px' }}>
            <Button
              appearance="outline"
              icon={<ChevronLeftRegular />}
              onClick={handleBack}
              disabled={currentStep === 1}
            >
              Back
            </Button>

            {currentStep < STEPS.length ? (
              <Button
                appearance="primary"
                icon={<ChevronRightRegular />}
                iconPosition="after"
                onClick={handleNext}
              >
                Next
              </Button>
            ) : (
              <Button
                appearance="primary"
                icon={<CheckmarkRegular />}
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? 'Submitting...' : 'Submit Onboarding Form'}
              </Button>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ClientOnboardingForm;
